// SP6 Task 5 — the last-published version's allocation target ids. AllocationSection
// uses this to lock the id input on a target that has already shipped: a
// published version is immutable history, and persisted allocation rows may
// already reference that id, so renaming/removing it out from under them
// would orphan those rows. Fetches once per jobTypeId via the same
// `formConfigApi.getCurrentPublished` every other Job Config surface uses to
// read published content (see useFormConfig.ts) — no bespoke fetch invented.
import { useEffect, useState } from 'react';
import { migrateContentToV3 } from '@capture/shared';
import { formConfigApi } from '../api/client';

/**
 * Empty set when `jobTypeId` is undefined, there is no published version yet,
 * or the fetch fails — the id-lock is a courtesy, not a guarantee, so a
 * network hiccup must never over-lock the table (that would silently block
 * legitimate edits).
 */
export function usePublishedTargetIds(jobTypeId: string | undefined): Set<string> {
  const [ids, setIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!jobTypeId) {
      setIds(new Set());
      return;
    }
    let cancelled = false;
    formConfigApi
      .getCurrentPublished(jobTypeId)
      .then((published) => {
        if (cancelled || !published) return;
        const v3 = migrateContentToV3(published.content);
        const targetIds = v3.remittance?.allocation?.targets.map((t) => t.id) ?? [];
        setIds(new Set(targetIds));
      })
      .catch(() => {
        if (!cancelled) setIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [jobTypeId]);

  return ids;
}
